/**
 * Service for Location Sharing API
 * Implements Snapchat-style mutual location sharing between contacts
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/location';

/**
 * Sharing modes
 */
export const SHARING_MODES = {
    PRECISE: 'precise',      // Exact location
    APPROXIMATE: 'approximate', // City level only
    GHOST: 'ghost'           // Hidden/invisible
};

/**
 * Permission statuses
 */
export const PERMISSION_STATUS = {
    ACTIVE: 'active',
    PENDING: 'pending',
    BLOCKED: 'blocked'
};

/**
 * Sharing mode labels (French)
 */
export const SHARING_MODE_LABELS = {
    precise: 'Position exacte',
    approximate: 'Ville uniquement',
    ghost: 'Mode fantôme'
};

/**
 * Normalize object keys to camelCase
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const newKey = key.charAt(0).toLowerCase() + key.slice(1);
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

// =====================================================
// Location Updates
// =====================================================

/**
 * Update current user's location
 * @param {Object} locationData - Location data from GPS
 * @returns {Promise<Object>} Result
 */
export const updateMyLocation = async (locationData) => {
    try {
        const { latitude, longitude, accuracy, altitude, speed, heading } = locationData;

        // Get battery level if available
        let batteryLevel = null;
        if (navigator.getBattery) {
            try {
                const battery = await navigator.getBattery();
                batteryLevel = Math.round(battery.level * 100);
            } catch (e) {
                // Battery API not available
            }
        }

        const response = await fetch(`${API_BASE_URL}/update`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                latitude,
                longitude,
                accuracy,
                altitude,
                speed,
                heading,
                batteryLevel
            })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error updating location:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Set user as inactive (stop sharing location)
 * @returns {Promise<Object>} Result
 */
export const setInactive = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/inactive`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error setting inactive:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

// =====================================================
// Location Sharing Permissions
// =====================================================

/**
 * Get all contacts visible on map (mutual sharing)
 * @returns {Promise<Array>} Array of contacts with their locations
 */
export const getVisibleContacts = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/contacts`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching visible contacts:', error);
        return [];
    }
};

/**
 * Get my sharing permissions (who can see my location)
 * @returns {Promise<Array>} Array of permissions
 */
export const getMySharePermissions = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/sharing`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching share permissions:', error);
        return [];
    }
};

/**
 * Get pending sharing requests (people who want to see my location)
 * @returns {Promise<Array>} Array of pending requests
 */
export const getPendingRequests = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/pending`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching pending requests:', error);
        return [];
    }
};

/**
 * Request to share location with a contact (sends request to them)
 * @param {number} contactId - The contact's people ID
 * @returns {Promise<Object>} Result
 */
export const requestLocationSharing = async (contactId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/request`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ contactId })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error requesting location sharing:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Accept a location sharing request
 * @param {number} requesterId - The person who requested to see my location
 * @param {string} sharingMode - How to share (precise, approximate, ghost)
 * @returns {Promise<Object>} Result
 */
export const acceptSharingRequest = async (requesterId, sharingMode = SHARING_MODES.PRECISE) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/accept`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ requesterId, sharingMode })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error accepting sharing request:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Reject/decline a location sharing request
 * @param {number} requesterId - The person who requested
 * @returns {Promise<Object>} Result
 */
export const rejectSharingRequest = async (requesterId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/reject`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ requesterId })
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error rejecting sharing request:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update sharing mode for a specific contact
 * @param {number} viewerId - The person who can see my location
 * @param {string} sharingMode - New sharing mode
 * @returns {Promise<Object>} Result
 */
export const updateSharingMode = async (viewerId, sharingMode) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/mode`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ viewerId, sharingMode })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error updating sharing mode:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Stop sharing location with a contact
 * @param {number} viewerId - The person to stop sharing with
 * @returns {Promise<Object>} Result
 */
export const stopSharingWith = async (viewerId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/stop`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ viewerId })
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error stopping location sharing:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Block a contact from ever requesting location
 * @param {number} contactId - The contact to block
 * @returns {Promise<Object>} Result
 */
export const blockContact = async (contactId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/permissions/block`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ contactId })
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error blocking contact:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

// =====================================================
// Location History
// =====================================================

/**
 * Get my location history
 * @param {Object} options - Query options (startDate, endDate, limit)
 * @returns {Promise<Array>} Location history entries
 */
export const getMyLocationHistory = async (options = {}) => {
    try {
        const { startDate, endDate, limit = 100 } = options;

        const params = new URLSearchParams({ limit: limit.toString() });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await fetch(`${API_BASE_URL}/history?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching location history:', error);
        return [];
    }
};

/**
 * Delete my location history
 * @param {string} beforeDate - Delete entries before this date (optional)
 * @returns {Promise<Object>} Result
 */
export const deleteMyLocationHistory = async (beforeDate = null) => {
    try {
        const body = beforeDate ? { beforeDate } : {};

        const response = await fetch(`${API_BASE_URL}/history`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(body)
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error deleting location history:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

// =====================================================
// Geolocation Utilities
// =====================================================

/**
 * Get current position from browser
 * @param {Object} options - Geolocation options
 * @returns {Promise<Object>} Position data
 */
export const getCurrentPosition = (options = {}) => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Géolocalisation non supportée par ce navigateur'));
            return;
        }

        const defaultOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 30000,
            ...options
        };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    altitude: position.coords.altitude,
                    altitudeAccuracy: position.coords.altitudeAccuracy,
                    heading: position.coords.heading,
                    speed: position.coords.speed,
                    timestamp: position.timestamp
                });
            },
            (error) => {
                let message;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message = 'Permission de géolocalisation refusée';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        message = 'Position non disponible';
                        break;
                    case error.TIMEOUT:
                        message = 'Délai de géolocalisation dépassé';
                        break;
                    default:
                        message = 'Erreur de géolocalisation inconnue';
                }
                reject(new Error(message));
            },
            defaultOptions
        );
    });
};

/**
 * Watch position continuously
 * @param {Function} onSuccess - Callback on position update
 * @param {Function} onError - Callback on error
 * @param {Object} options - Geolocation options
 * @returns {number} Watch ID (use to clear with clearWatch)
 */
export const watchPosition = (onSuccess, onError, options = {}) => {
    if (!navigator.geolocation) {
        onError(new Error('Géolocalisation non supportée par ce navigateur'));
        return null;
    }

    const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
        ...options
    };

    return navigator.geolocation.watchPosition(
        (position) => {
            onSuccess({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                heading: position.coords.heading,
                speed: position.coords.speed,
                timestamp: position.timestamp
            });
        },
        (error) => {
            let message;
            switch (error.code) {
                case error.PERMISSION_DENIED:
                    message = 'Permission de géolocalisation refusée';
                    break;
                case error.POSITION_UNAVAILABLE:
                    message = 'Position non disponible';
                    break;
                case error.TIMEOUT:
                    message = 'Délai de géolocalisation dépassé';
                    break;
                default:
                    message = 'Erreur de géolocalisation inconnue';
            }
            onError(new Error(message));
        },
        defaultOptions
    );
};

/**
 * Stop watching position
 * @param {number} watchId - Watch ID from watchPosition
 */
export const clearWatch = (watchId) => {
    if (watchId && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
    }
};

/**
 * Calculate distance between two points (Haversine formula)
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

/**
 * Format distance for display
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance
 */
export const formatDistance = (meters) => {
    if (meters < 1000) {
        return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * Format last seen time
 * @param {Date|string} date - Last seen timestamp
 * @returns {string} Formatted time
 */
export const formatLastSeen = (date) => {
    const lastSeen = new Date(date);
    const now = new Date();
    const diffMinutes = Math.floor((now - lastSeen) / (1000 * 60));

    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;

    return lastSeen.toLocaleDateString('fr-FR');
};

export default {
    // Location updates
    updateMyLocation,
    setInactive,

    // Permissions
    getVisibleContacts,
    getMySharePermissions,
    getPendingRequests,
    requestLocationSharing,
    acceptSharingRequest,
    rejectSharingRequest,
    updateSharingMode,
    stopSharingWith,
    blockContact,

    // History
    getMyLocationHistory,
    deleteMyLocationHistory,

    // Utilities
    getCurrentPosition,
    watchPosition,
    clearWatch,
    calculateDistance,
    formatDistance,
    formatLastSeen,

    // Constants
    SHARING_MODES,
    PERMISSION_STATUS,
    SHARING_MODE_LABELS
};
